import * as styles from "./TextEditorToolbar.css";

import { type JSX, type KeyboardEvent, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@frontend/components/atoms/Popover";
import { Toggle } from "@frontend/components/atoms/Toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { useLingui } from "@lingui/react/macro";
import { useEditorState } from "@tiptap/react";

import { useTextEditorContext } from "./TextEditor.context";

export function TextEditorLinkTool(): JSX.Element {
  const { t } = useLingui();

  const { editor, size, disabled, setToolbarHeld } = useTextEditorContext();

  const [open, setOpen] = useState(false);
  const [href, setHref] = useState("");

  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      active: editor?.isActive("link") ?? false,
      enabled: editor?.isEditable ?? false,
    }),
  });

  const label = t`Link`;

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen && editor !== null) {
      setHref(editor.getAttributes("link").href ?? "");
    }

    setToolbarHeld(nextOpen);

    setOpen(nextOpen);
  }

  function applyLink() {
    if (editor === null) {
      return;
    }

    const url = href.trim();

    if (url === "") {
      removeLink();

      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();

    handleOpenChange(false);
  }

  function removeLink() {
    editor?.chain().focus().extendMarkRange("link").unsetLink().run();

    handleOpenChange(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();

      applyLink();
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              render={
                <Toggle
                  size={size}
                  pressed={state?.active ?? false}
                  disabled={disabled || false === (state?.enabled ?? false)}
                  aria-label={label}
                >
                  <Icon name="Link" size={size} />
                </Toggle>
              }
            />
          }
        />

        <TooltipContent>{label}</TooltipContent>
      </Tooltip>

      <PopoverContent side="bottom" align="start" className={styles.linkForm}>
        <Input
          size="sm"
          type="url"
          autoFocus
          value={href}
          onValueChange={setHref}
          onKeyDown={handleKeyDown}
          placeholder="https://example.com"
          aria-label={t`Link address`}
        />

        <Button size="sm" variant="primary" onClick={applyLink}>
          {t`Apply`}
        </Button>

        {state?.active === true ? (
          <Button
            size="sm"
            variant="ghost"
            aria-label={t`Remove link`}
            onClick={removeLink}
          >
            <Icon name="Unlink" size="sm" />
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
